import AdminHostAvatar from '../../components/AdminHostAvatar';
import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, apiClient } from '../../App';
import AdminHeader from '../../components/AdminHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, ChevronLeft, ChevronRight, Users2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const toISO = (d) => d.toISOString().slice(0, 10);

const AdminHostList = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const today = new Date();
  const ago30 = new Date(today.getTime() - 30 * 86400000);

  const [q, setQ] = useState('');
  const [start, setStart] = useState(toISO(ago30));
  const [end, setEnd] = useState(toISO(today));
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/admin/hosts/overview-list', {
        params: { q: q || undefined, start, end, page, limit },
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load host list');
    } finally {
      setLoading(false);
    }
  }, [q, start, end, page, limit]);

  useEffect(() => {
    if (!token || !user || user.role !== 'admin') { navigate('/dashboard'); return; }
    fetchList();
  }, [page]); // eslint-disable-line

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else fetchList();
  };

  const onSearchKey = (e) => { if (e.key === 'Enter') applyFilters(); };

  const hosts = data?.hosts || [];
  const range = data?.range;
  const totalHosts = data?.total_hosts || 0;
  const totalPages = Math.max(1, Math.ceil(totalHosts / limit));

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <AdminHeader title="HOST INTELLIGENCE" onRefresh={fetchList} refreshing={loading} />

      <div className="mx-auto max-w-7xl px-4 py-6 space-y-5" data-testid="admin-host-list">
        <div>
          <h2 className="text-lg font-bold text-zinc-900">Host Activity</h2>
          <p className="text-sm text-zinc-500">
            Read-only aggregate KPIs per host. No player contact info shown here.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
            <div className="lg:col-span-1">
              <Label htmlFor="q" className="text-xs text-zinc-500">Search host</Label>
              <div className="relative">
                <Search className="w-4 h-4 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  id="q" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onSearchKey}
                  placeholder="username or email" className="pl-8"
                  data-testid="admin-host-list-search"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="start" className="text-xs text-zinc-500">Start (range KPIs)</Label>
              <Input id="start" type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="admin-host-list-start" />
            </div>
            <div>
              <Label htmlFor="end" className="text-xs text-zinc-500">End (range KPIs)</Label>
              <Input id="end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="admin-host-list-end" />
            </div>
            <div>
              <Button onClick={applyFilters} disabled={loading} className="bg-orange-500 hover:bg-orange-600 text-white w-full" data-testid="admin-host-list-apply-btn">
                {loading ? 'Loading…' : 'Apply'}
              </Button>
            </div>
          </div>
          {range && (
            <p className="text-xs text-zinc-400 mt-2">
              Range KPIs cover <span className="font-medium text-zinc-600">{range.start} → {range.end}</span>.
              <span className="ml-1">“Future scheduled” and “Active clubs” are all-time.</span>
            </p>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-zinc-500 border-b border-zinc-100 bg-zinc-50/60">
                  <th className="px-3 py-2.5">Host</th>
                  <th className="px-3 py-2.5" title="Sessions in selected date range">Sessions <span className="text-zinc-300">(range)</span></th>
                  <th className="px-3 py-2.5" title="Completed (past, not cancelled) in range">Completed <span className="text-zinc-300">(range)</span></th>
                  <th className="px-3 py-2.5" title="Cancelled in range">Cancelled <span className="text-zinc-300">(range)</span></th>
                  <th className="px-3 py-2.5" title="Rostered player slots in range">Player slots <span className="text-zinc-300">(range)</span></th>
                  <th className="px-3 py-2.5" title="Distinct rostered players in range">Unique players <span className="text-zinc-300">(range)</span></th>
                  <th className="px-3 py-2.5" title="Upcoming, not cancelled — all-time">Future <span className="text-zinc-300">(all-time)</span></th>
                  <th className="px-3 py-2.5" title="Clubs hosted — all-time">Clubs <span className="text-zinc-300">(all-time)</span></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-400">Loading hosts…</td></tr>
                )}
                {!loading && hosts.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-zinc-400" data-testid="admin-host-list-empty">No hosts found.</td></tr>
                )}
                {!loading && hosts.map((h) => (
                  <tr
                    key={h.id}
                    className="border-b border-zinc-50 hover:bg-orange-50/40 cursor-pointer transition-colors"
                    onClick={() => navigate(`/admin/hosts/${h.id}`)}
                    data-testid={`admin-host-row-${h.username}`}
                  >
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2.5">
                        <AdminHostAvatar imageUrl={h.profile_image_url} username={h.username} />
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-900 truncate">@{h.username}</div>
                          <div className="flex gap-1 mt-0.5">
                            {h.role && <span className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 rounded-full capitalize">{h.role}</span>}
                            {h.is_disabled && <span className="text-[10px] bg-red-100 text-red-700 px-1.5 rounded-full">Disabled</span>}
                            {h.is_test_user && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 rounded-full">Test</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 font-semibold text-zinc-900" data-testid={`admin-host-kpi-sessions-${h.username}`}>{h.total_sessions}</td>
                    <td className="px-3 py-3 text-zinc-700">{h.completed_sessions}</td>
                    <td className="px-3 py-3 text-zinc-700">{h.cancelled_sessions}</td>
                    <td className="px-3 py-3 text-zinc-700">{h.rostered_player_slots}</td>
                    <td className="px-3 py-3 text-zinc-700 flex items-center gap-1"><Users2 className="w-3.5 h-3.5 text-zinc-300" />{h.unique_players}</td>
                    <td className="px-3 py-3 text-zinc-700">{h.future_scheduled}</td>
                    <td className="px-3 py-3 text-zinc-700"><span className="inline-flex items-center gap-1"><Building2 className="w-3.5 h-3.5 text-zinc-300" />{h.active_clubs}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-3 py-2.5 border-t border-zinc-100 text-sm">
            <span className="text-zinc-500">{totalHosts} host{totalHosts === 1 ? '' : 's'} · page {data?.page || 1} / {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={loading || (data?.page || 1) <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="admin-host-list-prev-btn">
                <ChevronLeft className="w-4 h-4" /> Prev
              </Button>
              <Button variant="outline" size="sm" disabled={loading || (data?.page || 1) >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="admin-host-list-next-btn">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHostList;
